/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";

/**
 * One MCP server awaiting a decision.
 */
export type ApprovalRequestSummary = {
  /**
   * The resolved artifact identity. Absent when the server could not be identified, which must surface as unknown rather than as an absence of findings.
   */
  artifactRef?: string | undefined;
  /**
   * When the request was first raised.
   */
  createdAt: Date;
  /**
   * When the daily recheck first found the permission-relevant evidence differing from what the latest approval rested on. Absent when nothing has drifted. Cleared only by recording a new decision.
   */
  evidenceChangedAt?: Date | undefined;
  /**
   * The approval request ID.
   */
  id: string;
  /**
   * How many people have asked for this server.
   */
  requesterCount: number;
  /**
   * The Shadow MCP inventory page slug for a server_url target — the same identifier the inventory derives from the canonical URL, so a request links to the server page it describes. Absent for stdio targets.
   */
  serverSlug?: string | undefined;
  /**
   * The request's current status: unreviewed, requested, approved, denied, or superseded (the latest decision was explicitly displaced by a policy URL-list edit; history preserved, no enforcement derives from it until re-decided).
   */
  status: string;
  /**
   * The namespace of the requested reference, such as server_url or stdio_command.
   */
  targetKind: string;
  /**
   * The stored display form of the requested reference, with credential-shaped material (URL query strings and userinfo, secret-named flag and environment values in commands) redacted at intake.
   */
  targetRaw: string;
  /**
   * When the request last changed.
   */
  updatedAt: Date;
  /**
   * Whether the reference names an exact version.
   */
  versionPinned: boolean;
};

/** @internal */
export const ApprovalRequestSummary$inboundSchema: z.ZodMiniType<
  ApprovalRequestSummary,
  unknown
> = z.pipe(
  z.object({
    artifact_ref: z.optional(z.string()),
    created_at: z.pipe(
      z.iso.datetime({ offset: true }),
      z.transform(v => new Date(v)),
    ),
    evidence_changed_at: z.optional(
      z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
    ),
    id: z.string(),
    requester_count: z.int(),
    server_slug: z.optional(z.string()),
    status: z.string(),
    target_kind: z.string(),
    target_raw: z.string(),
    updated_at: z.pipe(
      z.iso.datetime({ offset: true }),
      z.transform(v => new Date(v)),
    ),
    version_pinned: z.boolean(),
  }),
  z.transform((v) => {
    return remap$(v, {
      "artifact_ref": "artifactRef",
      "created_at": "createdAt",
      "evidence_changed_at": "evidenceChangedAt",
      "requester_count": "requesterCount",
      "server_slug": "serverSlug",
      "target_kind": "targetKind",
      "target_raw": "targetRaw",
      "updated_at": "updatedAt",
      "version_pinned": "versionPinned",
    });
  }),
);

export function approvalRequestSummaryFromJSON(
  jsonString: string,
): SafeParseResult<ApprovalRequestSummary, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => ApprovalRequestSummary$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'ApprovalRequestSummary' from JSON`,
  );
}
